import { delay, eventChannel, END } from 'redux-saga'
import {
	all,
	select,
	call,
	put,
	fork,
	take,
	cancel,
	race,
	takeLatest,
} from 'redux-saga/effects'

import callAny from './helpers/call_any'
import formatError, { NearpodError } from './helpers/error_formatter'
import actionTypes from 'redux/actions/actionTypes'
import interactiveVideoActions from 'redux/actions/interactiveVideoActions'
import videoActions from 'redux/actions/videoActions'
import { interactiveVideoSync, assetSync, generalSync } from 'sync'
import { findVideoStatus, videoStatusTypes } from 'helpers/interactiveVideoStatus'
import {
	identitySelector,
	assetsSelector,
	getBreakpointFromClientIdSelector,
	getBreakpointFromUidSelector,
	getBreakpointActivityDataSelector,
	activitySaveSelector,
	currentActivitySelector,
	breakpointsSelector,
	videoStatusSelector,
	createContainerSelector,
	editDuplicationModeSelector,
	videoTrimmingSelector,
} from 'redux/selectors/interactive_video_creation'
import viewTypes from 'helpers/enums/viewTypes'
import binaryTypes from 'helpers/enums/interactiveVideo/binaryTypes'
import currentViewSelector from 'redux/selectors/ui/currentViewSelector'
import activityTypes from 'helpers/enums/activityTypes'
import { getMcqActivity } from 'redux/sagas/creationTool/mcqSagas'
import { getOeqActivity } from 'redux/sagas/creationTool/oeqSagas'
import videoSourceTypes from 'helpers/enums/interactiveVideo/videoSourceTypes'
import { getYoutubeVideosDetails } from './helpers/youtube'
import localErrorCodes from 'helpers/enums/errors/localErrorCodes'
import { localize } from 'helpers/translator'
import mediaSlide from 'redux/selectors/media_slides/find_media_slide'
import userRoleSelector from 'redux/selectors/user/user_role_selector'
import { validateUploadSize, maxUploadSizesByRole } from 'helpers/validation/upload_size'
import errorCodes from 'helpers/enums/errorCodes'
import interactiveVideoStatusTypes from 'helpers/enums/interactiveVideoCreation/videoStatusTypes'
import videoSizeTypes from 'helpers/enums/interactiveVideoCreation/videoSizeTypes'
import upgradeSourceTypes from 'helpers/enums/metrics/upgradeSourceTypes'
import editModes from 'helpers/enums/creationTool/editModes'
import mediaSelector from 'redux/selectors/interactive_video_creation/media_selector'
import myLibraryCurrentFolderSelector from 'redux/selectors/my_library/my_library_current_folder_selector'

function *getAsset() {
	const currentView = yield select(currentViewSelector)
	if ([
		viewTypes.INTERACTIVE_VIDEO_CREATE,
		viewTypes.VIDEO_CREATE,
		viewTypes.VIDEO_STANDALONE_CREATE,
	].includes(currentView)) {
		const interactiveVideoRequestData = yield select(identitySelector)
		const interactiveVideoAssets = yield select(assetsSelector)
		yield all([
			call(getAssetVideo, interactiveVideoAssets.videoUid, interactiveVideoRequestData.jwt),
			call(getAssetImage, interactiveVideoAssets.posterUid, interactiveVideoRequestData.jwt),
		])
	}
}

function *getAssetImage(posterUid, jwt) {
	try {
		const poster = yield callAny(interactiveVideoSync.getAssetImage, posterUid, jwt)
		yield put(interactiveVideoActions.interactiveVideoAssetPosterFetched(poster))
	} catch (error) {
		const formattedError = formatError(error)
		yield put(interactiveVideoActions.interactiveVideoAssetPosterFetchedFailed(
			formattedError
		))
	}
}

function *getAssetVideo(videoUid, jwt) {
	try {
		const video = yield callAny(interactiveVideoSync.getAssetVideo, videoUid, jwt)
		yield put(interactiveVideoActions.interactiveVideoAssetVideoFetched(video))
	} catch (error) {
		const formattedError = formatError(error)
		yield put(interactiveVideoActions.interactiveVideoAssetVideoFetchedFailed(
			formattedError
		))
	}
}

function *updateMedia(action) {
	try {
		const interactiveVideoIdentityData = yield select(identitySelector)
		yield all([
			callAny(
				interactiveVideoSync.updateMedia,
				interactiveVideoIdentityData.uid,
				interactiveVideoIdentityData.jwt,
				action.duration,
				action.credits,
			),
			// Send it only when we have the videoId setted,
			// to allow users create interactive videos and
			// upload the video with the video uploader
			// in the next page.
			action.videoId && callAny(
				interactiveVideoSync.createMediaAsset,
				interactiveVideoIdentityData.uid,
				interactiveVideoIdentityData.jwt,
				action.videoId
			),
		])
		yield put(interactiveVideoActions.updateVideoSuccess())
	} catch (error) {
		yield put(interactiveVideoActions.updateVideoFailed(
			formatError(error)
		))
	}
}

function *saveSlide() {
	try {
		yield call(callSaveSlide)
		yield put(interactiveVideoActions.saveInteractiveVideoSuccess())
	} catch (error) {
		yield put(interactiveVideoActions.saveInteractiveVideoFailed(
			formatError(error)
		))
	}
}

function *saveVideoSlide() {
	try {
		yield call(callSaveSlide)
		yield put(videoActions.saveVideoSuccess())
	} catch (error) {
		yield put(videoActions.saveVideoFailed(
			formatError(error)
		))
	}
}

function *saveSlideInEmbed(action) {
	try {
		yield call(callSaveSlide)
		yield put(interactiveVideoActions.saveInteractiveVideoEmbedSuccess(action.externalUid))
	} catch (error) {
		yield put(interactiveVideoActions.saveInteractiveVideoEmbedFailed(
			formatError(error)
		))
	}
}

function *callSaveSlide() {
	const interactiveVideoIdentityData = yield select(identitySelector)
	const trimmingData = yield select(videoTrimmingSelector)
	if (trimmingData.isTrimToolOpen && trimmingData.hasDifferentTrim) {
		throw new NearpodError(
			localErrorCodes.TRIM_CHANGE_NOT_SAVED,
		)
	} else {
		yield callAny(
			interactiveVideoSync.saveSlide,
			interactiveVideoIdentityData.externalUid,
			interactiveVideoIdentityData.jwt,
		)
	}
}

function *publishInteractiveVideo() {
	try {
		const interactiveVideoIdentityData = yield select(identitySelector)
		const response = yield callAny(
			interactiveVideoSync.publishInteractiveVideo,
			interactiveVideoIdentityData.externalUid,
		)
		yield put(interactiveVideoActions.publishInteractiveVideoSuccess(
			response.payload.redirectUrl
		))
	} catch (error) {
		yield put(interactiveVideoActions.publishInteractiveVideoError(
			formatError(error)
		))
	}
}

function *createInteractiveVideo() {
	try {
		const currentFolderUid = yield select(myLibraryCurrentFolderSelector)
		const response = yield callAny(interactiveVideoSync.createInteractiveVideo, currentFolderUid)
		yield put(interactiveVideoActions.createInteractiveVideoSuccess(
			response.payload,
		))
	} catch (error) {
		yield put(interactiveVideoActions.createInteractiveVideoFailed(
			formatError(error)
		))
	}
}

function *getMediaData() {
	try {
		const interactiveVideoIdentityData = yield select(identitySelector)
		const response =
			yield callAny(
				interactiveVideoSync.getInteractiveVideoData,
				interactiveVideoIdentityData.jwt,
				interactiveVideoIdentityData.uid,
				'raw',
			)
		yield put(
			interactiveVideoActions.mediaDataFetched(response)
		)
	} catch (error) {
		yield put(interactiveVideoActions.mediaDataFailed(
			formatError(error)
		))
	}
}

const createUploadEntity = (fileEntity, initializedVideo, initializedImage, microtime, externalUid) => {
	let emit
	const progressChannel = eventChannel(emitter => {
		emit = emitter
		return () => {}
	})
	const uploadPromise = assetSync.uploadVideo(
		fileEntity,
		initializedVideo.uid,
		initializedImage.uid,
		microtime,
		emit,
		externalUid,
	).finally(() => {
		emit(END)
	})

	return {
		uploadPromise,
		progressChannel,
	}
}

function *watchOnProgress(chan) {
	while (true) {
		const data = yield take(chan)
		const percent = Math.round(data.percent) || 0
		yield put(
			interactiveVideoActions.videoProgressEvent(
				percent,
			)
		)
	}
}

function *initializeAndUploadLocalVideo(videoVersions, size, screenshotVersions, fileEntity, externalUid) {

	const [initializedVideo, initializedImage, microtimeResponse] = yield all([
		callAny(
			assetSync.initializeVideo,
			videoVersions,
			size,
		),
		callAny(
			assetSync.initializeImage,
			screenshotVersions,
		),
		callAny(generalSync.getCurrentMicrotime),
	])

	const { uploadPromise, progressChannel } = createUploadEntity(
		fileEntity,
		initializedVideo,
		initializedImage,
		microtimeResponse.payload.microtime,
		externalUid,
	)
	yield fork(watchOnProgress, progressChannel)
	yield callAny(() => uploadPromise)

	return {
		video: initializedVideo,
		image: initializedImage,
	}
}

function *createExternalVideo(fileEntity) {
	const [video, image] = yield all([
		callAny(
			assetSync.createExternalVideo,
			fileEntity.type,
			fileEntity.id,
			fileEntity.duration,
			fileEntity.name,
		),
		callAny(
			assetSync.createExternalImage,
			fileEntity.type,
			fileEntity.id,
		),
	])

	return {
		video,
		image,
	}
}

function *createYoutubeVideo(fileEntity) {
	const [ videoDetails ] = yield call(getYoutubeVideosDetails, [fileEntity.id])

	const hydratedFileEntity = {
		...fileEntity,
		duration: videoDetails.duration,
	}

	return yield call(
		createExternalVideo,
		hydratedFileEntity
	)
}

function *uploadVideoMain() {
	while (true) {
		let action = yield take([actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_PICKED])
		const uploadSyncTask = yield fork(uploadVideo, action)
		action = yield take([
			actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_CANCEL,
			actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_FAILED,
			actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_SUCCESS,
		])
		if (action.type === actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_CANCEL) {
			yield cancel(uploadSyncTask)
		}
	}
}

function *uploadVideo(action) {
	try {
		const videoVersions = ['medium', 'large']
		const screenshotVersions = ['small', 'medium']
		const fileEntity = action.file.files[0]
		const size = action.file.files[0].size
		let initializedAsset
		let title
		const interactiveVideoCreationData = yield select(createContainerSelector)
		const interactiveVideoIdentityData = yield select(identitySelector)
		const userRole = yield select(userRoleSelector)
		const maxUploadSize = maxUploadSizesByRole(userRole)
		const isFileSizeValid = size ? validateUploadSize(size, userRole) : true

		if (!isFileSizeValid) {
			throw new NearpodError(
				errorCodes.EXCEED_MAX_FILE_SIZE,
				localize('uploader_max_video_size').replace('%s', maxUploadSize),
			)
		}

		yield callAny(
			interactiveVideoSync.checkStorage,
			interactiveVideoIdentityData.externalUid,
			size,
			interactiveVideoIdentityData.jwt,
		)

		if (fileEntity.type === videoSourceTypes.YOUTUBE) {
			title = fileEntity.name
			initializedAsset = yield call(
				createYoutubeVideo,
				fileEntity
			)
		} else {
			initializedAsset = yield call(
				initializeAndUploadLocalVideo,
				videoVersions,
				size,
				screenshotVersions,
				fileEntity,
				interactiveVideoCreationData.externalUid,
			)
		}

		yield callAny(
			interactiveVideoSync.linkAsset,
			interactiveVideoIdentityData.uid,
			interactiveVideoIdentityData.jwt,
			initializedAsset.video.uid,
			initializedAsset.image.uid,
		)

		yield call(
			copySettings,
			action.lessonUID,
			interactiveVideoIdentityData.externalUid,
			interactiveVideoIdentityData.jwt,
			!!title ? title : null,
		)

		yield put(interactiveVideoActions.uploadVideoSuccess(
			initializedAsset.video,
		))
	} catch (error) {
		const upgradeKey = `ADD_${action.file.files[0].type.toUpperCase()}_VIDEO`
		const upgradeSource = upgradeSourceTypes[upgradeKey]
		yield put(interactiveVideoActions.uploadVideoFailed(
			formatError(error),
			upgradeSource,
		))
	}
}

function *uploadPoster(action) {
	try {
		const screenshotVersions = ['small', 'medium']
		const initializedImage = yield callAny(
			assetSync.initializeImage,
			screenshotVersions,
		)

		yield callAny(
			assetSync.uploadPoster,
			action.file.files[0],
			initializedImage.uid,
		)

		const interactiveVideoIdentityData = yield select(identitySelector)
		yield callAny(
			interactiveVideoSync.linkPosterAsset,
			interactiveVideoIdentityData.uid,
			interactiveVideoIdentityData.jwt,
			initializedImage.uid,
		)
		yield put(interactiveVideoActions.uploadPosterSuccess(
			initializedImage,
		))
	} catch (error) {
		yield put(interactiveVideoActions.uploadPosterFailed(
			formatError(error)
		))
	}
}

function *copySettings(lessonUID, externalUid, jwt, title = '') {
	return yield callAny(
		interactiveVideoSync.copySettings,
		lessonUID,
		externalUid,
		jwt,
		title,
	)
}

function *copyMedia(action) {
	try {
		const interactiveVideoIdentityData = yield select(identitySelector)
		let mediaSlideSelector = yield select(mediaSlide)
		let mediaSlideSelected = mediaSlideSelector(action.lessonUID)

		while (!mediaSlideSelected.uid) {
			const mediaSlideFetch = yield take([
				actionTypes.MEDIA_SLIDE_FETCH_SUCCESS,
				actionTypes.MEDIA_SLIDE_FETCH_FAILED,
			])
			if (mediaSlideFetch.lessonUid === action.lessonUID) {
				switch (mediaSlideFetch.type) {
					case actionTypes.MEDIA_SLIDE_FETCH_SUCCESS:
						mediaSlideSelector = yield select(mediaSlide)
						mediaSlideSelected = mediaSlideSelector(action.lessonUID)
						break
					case actionTypes.MEDIA_SLIDE_FETCH_FAILED:
						return yield put(videoActions.copyFailed())
				}
			}
		}
		yield callAny(
			interactiveVideoSync.checkStorage,
			interactiveVideoIdentityData.externalUid,
			videoSizeTypes.STORE,
			interactiveVideoIdentityData.jwt,
		)

		yield callAny(
			interactiveVideoSync.copyMedia,
			interactiveVideoIdentityData.uid,
			mediaSlideSelected.uid,
			interactiveVideoIdentityData.jwt,
		)

		yield call(
			copySettings,
			action.lessonUID,
			interactiveVideoIdentityData.externalUid,
			interactiveVideoIdentityData.jwt,
		)

		yield put(videoActions.videoStoreLessonCopiedSuccess())
	} catch (error) {
		const upgradeSource = upgradeSourceTypes.ADD_STORE_VIDEO
		yield put(videoActions.videoStoreLessonCopiedFailed(
			formatError(error),
			upgradeSource,
		))
	}
}

function *uploadBinary(action, binaryType) {
	let binaryName = Object.keys(binaryTypes).find(key => binaryTypes[key] === binaryType) + '.pdf'
	binaryName = binaryName.toLowerCase()
	const initializedBinary = yield callAny(assetSync.initializeBinary, binaryName)
	yield callAny(
		assetSync.uploadBinary,
		action.file.files[0],
		initializedBinary.assetUid,
		binaryName,
	)

	const interactiveVideoIdentityData = yield select(identitySelector)
	return yield callAny(
		interactiveVideoSync.linkBinaryAsset,
		interactiveVideoIdentityData.uid,
		interactiveVideoIdentityData.jwt,
		initializedBinary.assetUid,
		binaryType,
	)
}

function *uploadTranscript(action) {
	try {
		const initializedBinary = yield call(uploadBinary, action, binaryTypes.TRANSCRIPT)
		yield put(interactiveVideoActions.uploadTranscriptSuccess(
			initializedBinary,
		))
	} catch (error) {
		yield put(interactiveVideoActions.uploadTranscriptFailed(
			formatError(error)
		))
	}
}

function *uploadQuestion(action) {
	try {
		const initializedBinary = yield call(uploadBinary, action, binaryTypes.QUESTION)
		yield put(interactiveVideoActions.uploadQuestionSuccess(
			initializedBinary,
		))
	} catch (error) {
		yield put(interactiveVideoActions.uploadQuestionFailed(
			formatError(error)
		))
	}
}

function *uploadCredits(action) {
	try {
		const interactiveVideoIdentityData = yield select(identitySelector)
		const response = yield callAny(
			interactiveVideoSync.updateEntity,
			interactiveVideoIdentityData.uid,
			interactiveVideoIdentityData.jwt,
			{credits: action.credits},
		)
		yield put(interactiveVideoActions.uploadCreditsSuccess(response.credits))
	} catch (error) {
		yield put(interactiveVideoActions.uploadCreditsFailed(
			formatError(error)
		))
	}
}

function *getPosterImage(action) {
	const currentView = yield select(currentViewSelector)
	if (currentView !== viewTypes.INTERACTIVE_VIDEO_SCREENSHOT) {
		return
	}
	const interactiveVideoIdentityData = yield select(identitySelector)
	yield call(
		getAssetImage,
		action.data.posterUid,
		interactiveVideoIdentityData.jwt
	)
}

function *checkVideoStatusMain(action) {
	yield race({
		task: call(checkVideoStatus, action),
		cancel: take(actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_CANCEL),
	})
}

function *checkVideoStatus(action) {
	try {
		let response
		let videoStatus = videoStatusTypes.PROCESSING
		while (videoStatus === videoStatusTypes.PROCESSING) {
			response = yield callAny(
				assetSync.getVideoStatus,
				action.payload.uid,
			)
			videoStatus = findVideoStatus(response.versions)
			if (videoStatus === videoStatusTypes.FAILED) {
				throw new NearpodError()
			}
			if (videoStatus === videoStatusTypes.SUCCESS) {
				yield put(interactiveVideoActions.uploadVideoStatusSuccess())
			}
			if (videoStatus === videoStatusTypes.PROCESSING) {
				yield call(delay, 3000)
			}
		}
	} catch (error) {
		const serverError = formatError(error)
		yield put(interactiveVideoActions.uploadVideoStatusFailed(serverError))
	}
}

function *breakpointCreation(breakpointClientId) {
	const interactiveVideoIdentityData = yield select(identitySelector)
	const interactiveVideoBreakpointData = yield select(getBreakpointFromClientIdSelector(breakpointClientId))

	const addBreakpointResponse = yield callAny(
		interactiveVideoSync.addBreakpoint,
		interactiveVideoIdentityData.uid,
		interactiveVideoIdentityData.jwt,
		interactiveVideoBreakpointData.startTime,
		interactiveVideoBreakpointData.endTime,
	)

	return addBreakpointResponse
}

function *activityCreation(action) {
	try {
		const addBreakpointResponse = yield call(
			breakpointCreation,
			action.breakpointClientId
		)
		yield put(interactiveVideoActions.createActivitySuccess(
			action.activityType,
			action.breakpointClientId,
			addBreakpointResponse.uid,
		))
	} catch (error) {
		yield put(interactiveVideoActions.createActivityError(
			action.breakpointClientId,
			formatError(error),
		))
	}
}

function *getVideoSettings() {
	try {
		const interactiveVideoIdentityData = yield select(identitySelector)
		const response =
			yield callAny(
				interactiveVideoSync.getPresentation,
				interactiveVideoIdentityData.presentationUid
			)
		yield put(
			interactiveVideoActions.getVideoSettingsSuccess(response.payload)
		)
	} catch (error) {
		yield put(interactiveVideoActions.getVideoSettingsFailed(
			formatError(error)
		))
	}
}

function *saveVideoSettings(action) {
	try {
		const interactiveVideoIdentityData = yield select(identitySelector)
		const status = yield select(videoStatusSelector)
		if (!action.payload.title) {
			throw new NearpodError(
				localErrorCodes.VALIDATION_ERROR,
				localize('video_settings_untitled_error'),
			)
		}
		yield callAny(
			interactiveVideoSync.savePresentation,
			interactiveVideoIdentityData.jwt,
			action.payload.title,
			action.payload.subjectSelected,
			action.payload.gradesSelected,
			interactiveVideoIdentityData.presentationUid
		)

		if (status === interactiveVideoStatusTypes.SAVING) {
			yield callAny(
				interactiveVideoSync.publishInteractiveVideo,
				interactiveVideoIdentityData.externalUid,
			)
		}
		yield put(
			interactiveVideoActions.saveVideoSettingsSuccess(action.payload, status)
		)
	} catch (error) {
		yield put(interactiveVideoActions.saveVideoSettingsFailed(
			formatError(error)
		))
	}
}

function *editActivity(action) {
	try {
		const interactiveVideoIdentityData = yield select(identitySelector)
		const breakpointActivityData =
			yield select(
				getBreakpointActivityDataSelector(
					action.breakpointActivityUid
				)
			)
		let newActivityUid = breakpointActivityData.activityUid
		const editMode = yield select(editDuplicationModeSelector)
		if (editMode === editModes.DUPLICATE_ON_EDIT) {
			const response = yield callAny(
				interactiveVideoSync.duplicateQuestion,
				interactiveVideoIdentityData.jwt,
				breakpointActivityData.activityUid
			)
			newActivityUid = response.payload.uid
		}

		yield put(
			interactiveVideoActions.editActivitySuccess(
				breakpointActivityData.activityType,
				newActivityUid,
				action.breakpointActivityUid,
			)
		)
	} catch (error) {
		yield put(interactiveVideoActions.editActivityError(
			formatError(error)
		))
	}
}

function *saveInteractiveVideoActivity(activityUid) {
	const saveData = yield select(activitySaveSelector)
	const mediaUid = saveData.mediaUid
	const jwt = saveData.jwt
	const breakpointyUid = saveData.breakpointUid
	const breakpointActivityUid = saveData.breakpointActivityUid
	const isEdition = saveData.isEdition

	yield all([
		// On edit there is no need to save the breakpoint
		// And we don't even have the breakpointyUid so we skip this
		!isEdition ? callAny(
			interactiveVideoSync.saveBreakpoint,
			mediaUid,
			breakpointyUid,
			jwt,
		) : null,
		callAny(
			interactiveVideoSync.saveBreakpointActivity,
			mediaUid,
			breakpointActivityUid,
			activityUid,
			jwt,
		),
	])

	return isEdition
}

function *getActivitiesData(activities) {
	const interactiveVideoIdentityData = yield select(identitySelector)

	const getActivityServices = {
		[activityTypes.OPEN_ENDED_QUESTION]: getOeqActivity,
		[activityTypes.MULTIPLE_CHOICE_QUESTION]: getMcqActivity,
	}

	const activitiesCalls = activities.map(activity => call(
		getActivityServices[activity.activityType],
		activity.activityUid,
		interactiveVideoIdentityData.jwt
	))

	yield all(activitiesCalls)
}

function *handleMediaDataFetched(action) {
	yield call(getPosterImage, action)

	const activities = []
	action.data.breakpoints.forEach(breakpoint => {
		breakpoint.activities.forEach(activity => {
			activities.push({
				activityUid: activity.activityUid,
				activityType: activity.activityType,
			})
		})
	})
	yield call(getActivitiesData, activities)
}

function *deleteActivity() {
	try {
		const data = yield select(identitySelector)
		const currentActivity = yield select(currentActivitySelector)
		const mediaUid = data.uid
		const jwt = data.jwt
		const breakpointUid = currentActivity.interactiveVideoActivity.breakpointUid

		yield callAny(
			interactiveVideoSync.deleteBreakpoint,
			mediaUid,
			breakpointUid,
			jwt,
		)

		yield put(
			interactiveVideoActions.activityDeleteSuccess(breakpointUid)
		)

	} catch (error) {
		const formattedError = formatError(error)
		yield put(interactiveVideoActions.activityDeleteFailed(formattedError))
	}
}

function *saveBreakpointPosition(action) {
	try {
		const data = yield select(identitySelector)
		const mediaUid = data.uid
		const jwt = data.jwt
		const breakpointData = yield select(getBreakpointFromUidSelector(action.breakpointUid))

		yield callAny(
			interactiveVideoSync.saveBreakpoint,
			mediaUid,
			action.breakpointUid,
			jwt,
			{
				startTime: breakpointData.startTime,
				endTime: breakpointData.endTime,
			},
		)

		yield put(
			interactiveVideoActions.saveBreakpointPositionSuccess()
		)

	} catch (error) {
		const formattedError = formatError(error)
		yield put(interactiveVideoActions.saveBreakpointPositionFailed(formattedError))
	}
}

function *getPreviewPosterImage(action) {
	if (action.data) {
		yield call(
			getAssetImage,
			action.data.mediaResponse.posterUid,
			action.data.jwt
		)
	}
}

function *saveStoreParentId(action) {
	const interactiveVideoIdentityData = yield select(identitySelector)
	try {
		yield callAny(
			interactiveVideoSync.saveStoreParentId,
			action.lessonUID,
			interactiveVideoIdentityData.externalUid,
			interactiveVideoIdentityData.jwt
		)
	} catch (error) {
		const formattedError = formatError(error)
		yield put(interactiveVideoActions.saveStoreParentIdFailed(formattedError))
	}
}

function *unlinkMediaAsset() {
	try {
		const data = yield select(identitySelector)
		const mediaUid = data.uid
		const jwt = data.jwt

		// @TODO: Improve this code once we have a better patching strategy on backend
		const breakpoints = yield select(breakpointsSelector)
		const breakpointsCall = breakpoints.map(breakpoint => callAny(
			interactiveVideoSync.deleteBreakpoint,
			mediaUid,
			breakpoint.uid,
			jwt
		))
		yield all(breakpointsCall)

		const response = yield callAny(
			interactiveVideoSync.updateEntity,
			mediaUid,
			jwt,
			{
				asset: {uid: ''},
				duration: 0,
				posterUid: '',
				credits: '',
			},
		)

		yield put(interactiveVideoActions.assetUnlinkSuccess(response))
	} catch (error) {
		yield put(interactiveVideoActions.assetUnlinkFailed())
	}
}

function *cancelVideoUpload() {
	// TODO: If the user cancels the video just when it ends uploading and is still not
	// associted and the patch reaches the service first, then the asset will not be
	// deasociated. Is pending to do something to handle that scenario when we can.
	try {
		const data = yield select(identitySelector)
		const mediaUid = data.uid
		const jwt = data.jwt

		yield callAny(
			interactiveVideoSync.patchUpdateMedia,
			mediaUid,
			jwt,
		)

		yield put(
			interactiveVideoActions.uploadVideoCancelSuccess()
		)

	} catch (error) {
		const formattedError = formatError(error)
		yield put(interactiveVideoActions.saveBreakpointPositionFailed(formattedError))
	}
}

function *saveTrimAndSlide() {
	const currentView = yield select(currentViewSelector)
	yield call(saveTrim)
	if (currentView === viewTypes.VIDEO_CREATE) {
		yield call(saveVideoSlide)
	} else {
		yield call(saveSlide)
	}
}

function *saveTrim() {
	try {
		const identity = yield select(identitySelector)
		const mediaUid = identity.uid
		const jwt = identity.jwt
		const media = yield select(mediaSelector)

		yield callAny(interactiveVideoSync.saveTrim, mediaUid, jwt, media.trimStart, media.trimEnd)
		yield put(interactiveVideoActions.trimToolSaveTrimSuccess())
	} catch (error) {
		const formattedError = formatError(error)
		yield put(interactiveVideoActions.trimToolSaveTrimFailed(formattedError))
	}
}

export default [
	takeLatest(actionTypes.INTERACTIVE_VIDEO_PLAYER_INITIALIZED,
		getAsset),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_SUBMIT_SELECT_FORM,
		updateMedia),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_SAVE,
		saveSlide),
	takeLatest(actionTypes.VIDEO_SAVE,
		saveVideoSlide),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_SAVE_SUCCESS,
		publishInteractiveVideo),
	takeLatest([
		actionTypes.MY_LIBRARY_CREATE_INTERACTIVE_VIDEO,
		actionTypes.MAIN_TOPBAR_CREATE_INTERACTIVE_VIDEO,
	], createInteractiveVideo),
	takeLatest([
		actionTypes.INTERACTIVE_VIDEO_INITIALIZE_CREATION_TOOL,
		actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_STATUS_SUCCESS,
		actionTypes.INTERACTIVE_VIDEO_SLIDE_DATA_FETCHED,
		actionTypes.INTERACTIVE_VIDEO_INITIALIZE_SCREENSHOT_ROUTE,
		actionTypes.INTERACTIVE_VIDEO_CREATION_BREAKPOINT_SAVE_POSITION_ERROR,
		actionTypes.INTERACTIVE_VIDEO_INITIALIZE_CREATION_TOOL_EDIT,
		actionTypes.VIDEO_INITIALIZE_EDIT_ROUTE,
		actionTypes.INTERACTIVE_VIDEO_INITIALIZE_CREATION_TOOL_BASE,
		actionTypes.VIDEO_INITIALIZE_CREATION_TOOL_BASE,
		actionTypes.VIDEO_INITIALIZE_PICKER_ROUTE,
		actionTypes.VIDEO_STORE_LESSON_COPIED_SUCCESS,
		actionTypes.VIDEO_STANDALONE_INITIALIZE_PICKER_ROUTE,
		actionTypes.VIDEO_STANDALONE_INITIALIZE_EDIT_ROUTE,
		actionTypes.VIDEO_STANDALONE_INITIALIZE_BASE,
	], getMediaData),
	fork(uploadVideoMain),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_UPLOAD_POSTER_PICKED,
		uploadPoster),
	takeLatest([
		actionTypes.VIDEO_STORE_LESSON_SELECTED,
		actionTypes.VIDEO_MY_VIDEOS_LESSON_SELECTED,
	], copyMedia),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_UPLOAD_HANDOUT_TRANSCRIPT_PICKED,
		uploadTranscript),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_UPLOAD_HANDOUT_QUESTION_PICKED,
		uploadQuestion),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_MEDIA_DATA_FETCHED,
		handleMediaDataFetched),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_SUCCESS,
		checkVideoStatusMain),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_UPLOAD_CREDITS,
		uploadCredits),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_ACTIVITY_CREATE,
		activityCreation),
	takeLatest([
		actionTypes.INTERACTIVE_VIDEO_INITIALIZE_CREATION_TOOL,
		actionTypes.INTERACTIVE_VIDEO_INITIALIZE_CREATION_TOOL_EDIT,
	], getVideoSettings),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_CREATION_SETTINGS_SAVE,
		saveVideoSettings),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_CREATION_EDIT_ACTIVITY,
		editActivity),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_DELETE_CONFIRMED,
		deleteActivity),
	takeLatest(actionTypes.MEDIA_SLIDE_VIDEO_PREVIEW_SLIDE_FETCH_SUCCESS,
		getPreviewPosterImage),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_CREATION_BREAKPOINT_SAVE_POSITION,
		saveBreakpointPosition),
	takeLatest([
		actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_PICKED,
		actionTypes.VIDEO_STORE_LESSON_SELECTED,
		actionTypes.VIDEO_MY_VIDEOS_LESSON_SELECTED,
	], saveStoreParentId),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_GOTO_PICKER,
		unlinkMediaAsset),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_CANCEL,
		cancelVideoUpload),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_CREATION_EMBED_IN_SLIDE,
		saveSlideInEmbed),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_CREATION_TRIM_TOOL_SAVE_TRIM,
		saveTrim),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_CREATION_TRIM_TOOL_UNSAVED_TRIM_AND_SAVE,
		saveTrimAndSlide),
]

export {
	getAsset,
	getAssetImage,
	getAssetVideo,
	updateMedia,
	getPosterImage,
	saveSlide,
	saveVideoSlide,
	publishInteractiveVideo,
	getMediaData,
	uploadVideo,
	uploadPoster,
	copyMedia,
	getVideoSettings,
	saveVideoSettings,
	editActivity,
	saveInteractiveVideoActivity,
	handleMediaDataFetched,
	getActivitiesData,
	deleteActivity,
	uploadBinary,
	uploadTranscript,
	uploadQuestion,
	saveBreakpointPosition,
	cancelVideoUpload,
	saveTrim,
	createInteractiveVideo,
}
